import type { MessageCompiler, MessageContext } from "vue-i18n";

// The app's message compiler, in its own module so tests can compile messages
// exactly as production does without importing the whole message bundle. The
// vue-i18n import is type-only, so it costs nothing at runtime and survives a
// test's `vi.mock("vue-i18n")`.
//
// It was introduced because vue-i18n 9's default compiler generated code with
// the Function constructor, which our production CSP (`script-src 'self'
// 'unsafe-inline'`, no `unsafe-eval`) blocks. Since vue-i18n 10 the default
// compiler interprets an AST instead, so the CSP no longer requires this; it
// stays to pin message syntax to `{named}` placeholders only (no plurals, no
// linked messages `@:`, no list `{0}`) and to keep rendered text exactly as it
// was. The built-in compiler is dropped from the bundle
// (`__INTLIFY_DROP_MESSAGE_COMPILER__` in vue.config.js), so this is the only
// one. `__tests__/i18nMessageSyntax.test.ts` fails on syntax it cannot render.

/**
 * Replaces each `{name}` in `message` with `lookup(name)`. A null or undefined
 * value renders as the empty string.
 */
export function interpolateNamed(
  message: string,
  lookup: (name: string) => unknown,
): string {
  return message.replace(/\{(\w+)\}/g, (_, name: string) => {
    const value = lookup(name);
    return value == null ? "" : String(value);
  });
}

export const cspSafeMessageCompiler: MessageCompiler = (message) => {
  if (typeof message !== "string") {
    return () => String(message ?? "");
  }
  if (!message.includes("{")) {
    return () => message;
  }
  return (ctx: MessageContext) =>
    interpolateNamed(message, (name) => ctx.named(name));
};
